import React, { useMemo } from 'react';
import { Card } from 'primereact/card';
import { useTranslation } from 'react-i18next';
import { RTUData, ServerPortEntry, ServerPortSnapshot } from '../../types/RtuData';
import { StaticCompositeTable } from './StaticCompositeTable';
import { formatServerPortServiceName } from '../../utils/rtuStaticDisplay';

interface ServerPortRow {
  name: string;
  config: string;
  port: string;
}

/**
 * Derives ordered rows from a ServerPortSnapshot.
 * Iterates entries in insertion order (which mirrors the numeric suffix
 * ordering that the backend preserves when mapping port_1 … port_N).
 */
function deriveRows(snapshot: ServerPortSnapshot | undefined): ServerPortRow[] {
  if (!snapshot?.entries) return [];

  return Object.values(snapshot.entries)
    .map((entry: ServerPortEntry): ServerPortRow => ({
      name:   formatServerPortServiceName(entry.name),
      config: entry.config ?? '—',
      port:   entry.port   ?? '—',
    }));
}

interface Props {
  data: RTUData | null | undefined;
}

/**
 * RtuServerPortsCard
 *
 * Displays the server_port composite block from RTU static data as a
 * structured table (Service / Config / Port).
 */
export const RtuServerPortsCard = ({ data }: Props) => {
  const { t } = useTranslation();

  const rows = useMemo(() => deriveRows(data?.rtuStaticData?.serverPort), [data]);

  return (
    <Card
      title={t('rtuDetail.staticData.serverPorts.title', { defaultValue: 'Server Ports' })}
      className="compact-card flat-card rtu-static-module-card"
    >
      <StaticCompositeTable
        rows={rows}
        columns={[
          { field: 'name',   header: t('rtuDetail.staticData.serverPorts.colName',   { defaultValue: 'Service' }) },
          {
            field: 'config',
            header: t('rtuDetail.staticData.serverPorts.colConfig', { defaultValue: 'Config' }),
            width: '6rem',
            statusTone: true,
            statusIconTooltips: {
              positive: 'rtuDetail.staticData.serverPorts.statusIcon.positive',
              negative: 'rtuDetail.staticData.serverPorts.statusIcon.negative',
            },
          },
          { field: 'port',   header: t('rtuDetail.staticData.serverPorts.colPort',   { defaultValue: 'Port'    }), width: '5rem' },
        ]}
        emptyMessage={t('table.noRowsFound', { defaultValue: 'No server port entries.' })}
      />
    </Card>
  );
};

export default RtuServerPortsCard;
